(function () {
  "use strict";

  if (window.MarineMaxCoveoCommerceTransport) return;

  function installCoveoCommerceTransport(dependencies) {
    const deps = dependencies || {};
    const runtimeWindow = deps.windowObject || globalThis.window;
    const runtimeDocument = deps.documentObject || globalThis.document;
    const requestPipeline = deps.requestPipeline;
    const missingDependencies = [];

    if (!runtimeWindow) missingDependencies.push("windowObject");
    if (!runtimeDocument) missingDependencies.push("documentObject");
    if (typeof runtimeWindow?.fetch !== "function") {
      missingDependencies.push("windowObject.fetch");
    }
    if (typeof requestPipeline?.classifyRequest !== "function") {
      missingDependencies.push("requestPipeline.classifyRequest");
    }
    if (typeof requestPipeline?.patchRequestBody !== "function") {
      missingDependencies.push("requestPipeline.patchRequestBody");
    }
    if (typeof requestPipeline?.recordRequestDebug !== "function") {
      missingDependencies.push("requestPipeline.recordRequestDebug");
    }
    if (
      typeof deps.addCoveoProductsToCollectionMembership !== "function"
    ) {
      missingDependencies.push("addCoveoProductsToCollectionMembership");
    }
    if (typeof deps.requestManagedRangeDomainRefresh !== "function") {
      missingDependencies.push("requestManagedRangeDomainRefresh");
    }
    if (missingDependencies.length) {
      throw new Error(
        `[Coveo Commerce Transport] Missing dependencies: ${missingDependencies.join(", ")}`,
      );
    }

    if (runtimeWindow.__coveoCommerceTransportInterceptorInstalled) {
      return (
        runtimeWindow.__coveoCommerceTransportInterceptorState ||
        Object.freeze({ installed: true, reused: true })
      );
    }

    const {
      addCoveoProductsToCollectionMembership,
      requestManagedRangeDomainRefresh,
    } = deps;

    function getCollectionRoot() {
      return runtimeDocument.getElementById("coveo-headless-collection");
    }

    function getLiveListingTemplate(token) {
      if (!token) return null;
      const template =
        getCollectionRoot()?._coveoCollectionListingTemplateRequest;
      return template?.token === token ? template : null;
    }

    function scheduleManagedRangeDomainRefresh() {
      runtimeWindow.setTimeout(function () {
        requestManagedRangeDomainRefresh({ invalidate: false });
      }, 0);
    }

    function captureFetchListingTemplateResponse(response, token) {
      const template = getLiveListingTemplate(token);
      if (!template) return;
      template.successful = response?.ok !== false;
      template.failed = response?.ok === false;
      if (!template.successful || typeof response?.clone !== "function") return;

      template.responsePromise = response
        .clone()
        .json()
        .then(function (payload) {
          const liveTemplate = getLiveListingTemplate(token);
          if (!liveTemplate) return null;
          liveTemplate.responsePayload = payload;
          return payload;
        })
        .catch(function () {
          return null;
        });
    }

    function captureXhrListingTemplateResponse(xhr, token) {
      const template = getLiveListingTemplate(token);
      if (!template) return;
      template.successful = xhr.status >= 200 && xhr.status < 400;
      template.failed = !template.successful;
      if (!template.successful) return;
      try {
        template.responsePayload = JSON.parse(String(xhr.responseText || "{}"));
      } catch (_) { }
    }

    function getCollectionMembershipProducts(payload) {
      const products =
        payload?.products ||
        payload?.results ||
        payload?.response?.products ||
        payload?.response?.results ||
        [];
      const resultItems = Array.isArray(payload?.results)
        ? payload.results
        : [];
      return Array.isArray(products)
        ? products.concat(resultItems)
        : resultItems;
    }

    function dispatchCollectionMembershipProgress() {
      try {
        const EventConstructor = runtimeWindow.CustomEvent;
        if (typeof EventConstructor !== "function") return;
        runtimeWindow.dispatchEvent(
          new EventConstructor("coveo:collection-membership-progress"),
        );
      } catch (_) { }
    }

    function captureCollectionPrefetchMembership(response, requestState) {
      const root = getCollectionRoot();
      if (
        !requestState.isCollectionScopePrefetchRequest ||
        requestState.isCollectionMembershipPageRequest ||
        !root
      ) {
        return;
      }
      try {
        const payloadPromise = response?.clone?.()?.json?.();
        payloadPromise
          ?.then?.(function (payload) {
            const membershipProducts = getCollectionMembershipProducts(payload);
            if (!membershipProducts.length) return;
            root._coveoCollectionScopeMembership =
              addCoveoProductsToCollectionMembership(
                root._coveoCollectionScopeMembership,
                membershipProducts,
                root.dataset.collectionHandle,
              );
            dispatchCollectionMembershipProgress();
          })
          ?.catch?.(function () { });
      } catch (_) { }
    }

    function prepareCoveoRequest(options) {
      const opts = options || {};
      const requestUrl = String(opts.url || "");
      const requestState = {
        bodyText: opts.bodyText,
        collectionListingTemplateToken: null,
        isCollectionMembershipPageRequest: false,
        isCollectionScopePrefetchRequest: false,
        isCoveoRequest: false,
        patched: false,
        shouldRefreshManagedRangeDomains: false,
      };
      if (
        !requestUrl.includes("coveo") ||
        typeof opts.bodyText !== "string"
      ) {
        return requestState;
      }

      requestState.isCoveoRequest = true;
      let body;
      try {
        body = JSON.parse(opts.bodyText);
        const request = requestPipeline.classifyRequest(requestUrl, body);
        requestState.isCollectionScopePrefetchRequest = !!(
          request.isListingEndpoint &&
          runtimeWindow.__coveoCollectionScopePrefetchLoading
        );
        requestState.isCollectionMembershipPageRequest = !!(
          body._mmCollectionMembershipPage === true ||
          opts.isCollectionMembershipPageTransport
        );
        if (opts.isFacetValuesOnlyTransport) {
          body._mmFacetValuesOnly = true;
          body._mmFacetValuesOnlyTargetId = String(
            opts.facetValuesOnlyTargetId || "",
          );
        }
        requestState.shouldRefreshManagedRangeDomains = !!(
          !opts.isFacetValuesOnlyTransport &&
          body._mmFacetValuesOnly !== true &&
          request.isSearchOrListingEndpoint &&
          !requestState.isCollectionScopePrefetchRequest
        );

        if (requestPipeline.patchRequestBody(requestUrl, body, opts.initLike)) {
          requestState.collectionListingTemplateToken =
            body.__mmCollectionListingTemplateToken || null;
          requestState.bodyText = JSON.stringify(body);
          requestState.patched = true;
        }
        if (
          !requestState.isCollectionScopePrefetchRequest &&
          !requestState.isCollectionMembershipPageRequest
        ) {
          requestPipeline.recordRequestDebug(
            requestUrl,
            body,
            opts.transport,
          );
        }
      } catch (_) { }
      return requestState;
    }

    const nativeFetch = runtimeWindow.fetch.bind(runtimeWindow);
    runtimeWindow.fetch = function coveoCommerceFetch(input, init) {
      const url = typeof input === "string" ? input : input?.url || "";
      const requestState = prepareCoveoRequest({
        bodyText: init?.body,
        facetValuesOnlyTargetId: init?._mmFacetValuesOnlyTargetId,
        initLike: init,
        isCollectionMembershipPageTransport:
          init?._mmCollectionMembershipPage === true,
        isFacetValuesOnlyTransport: init?._mmFacetValuesOnly === true,
        transport: "fetch",
        url,
      });
      const forwardedInit = requestState.patched
        ? Object.assign({}, init, { body: requestState.bodyText })
        : init;
      const responsePromise = nativeFetch(input, forwardedInit);
      if (!requestState.isCoveoRequest || !responsePromise?.then) {
        return responsePromise;
      }

      return responsePromise.then(function (response) {
        captureFetchListingTemplateResponse(
          response,
          requestState.collectionListingTemplateToken,
        );
        if (
          requestState.shouldRefreshManagedRangeDomains &&
          response?.ok !== false
        ) {
          scheduleManagedRangeDomainRefresh();
        }
        captureCollectionPrefetchMembership(response, requestState);
        return response;
      });
    };

    const NativeXMLHttpRequest = runtimeWindow.XMLHttpRequest;
    if (NativeXMLHttpRequest) {
      function CoveoCommerceXMLHttpRequest() {
        const xhr = new NativeXMLHttpRequest();
        let openUrl = "";
        let openMethod = "POST";
        const requestHeaders = {};
        const nativeOpen = xhr.open;
        const nativeSetRequestHeader = xhr.setRequestHeader;
        const nativeSend = xhr.send;

        xhr.open = function (method, url) {
          openUrl = String(url || "");
          openMethod = String(method || "POST").toUpperCase();
          return nativeOpen.apply(this, arguments);
        };
        xhr.setRequestHeader = function (name, value) {
          requestHeaders[String(name)] = String(value);
          return nativeSetRequestHeader.apply(this, arguments);
        };
        xhr.send = function (body) {
          const requestState = prepareCoveoRequest({
            bodyText: body,
            initLike: {
              method: openMethod,
              headers: requestHeaders,
            },
            transport: "xhr",
            url: openUrl,
          });
          if (requestState.patched) body = requestState.bodyText;

          if (requestState.collectionListingTemplateToken) {
            xhr.addEventListener(
              "loadend",
              function () {
                captureXhrListingTemplateResponse(
                  xhr,
                  requestState.collectionListingTemplateToken,
                );
              },
              { once: true },
            );
          }
          if (requestState.shouldRefreshManagedRangeDomains) {
            xhr.addEventListener(
              "loadend",
              function () {
                if (!(xhr.status >= 200 && xhr.status < 400)) return;
                scheduleManagedRangeDomainRefresh();
              },
              { once: true },
            );
          }
          return nativeSend.call(this, body);
        };
        return xhr;
      }

      CoveoCommerceXMLHttpRequest.prototype = NativeXMLHttpRequest.prototype;
      try {
        Object.setPrototypeOf(
          CoveoCommerceXMLHttpRequest,
          NativeXMLHttpRequest,
        );
      } catch (_) { }
      runtimeWindow.XMLHttpRequest = CoveoCommerceXMLHttpRequest;
    }

    const transportState = Object.freeze({
      installed: true,
      interceptsFetch: true,
      interceptsXMLHttpRequest: !!NativeXMLHttpRequest,
    });
    Object.defineProperty(
      runtimeWindow,
      "__coveoCommerceTransportInterceptorState",
      {
        configurable: false,
        enumerable: false,
        value: transportState,
        writable: false,
      },
    );
    runtimeWindow.__coveoCommerceTransportInterceptorInstalled = true;
    return transportState;
  }

  Object.defineProperty(window, "MarineMaxCoveoCommerceTransport", {
    configurable: false,
    enumerable: false,
    value: Object.freeze({ install: installCoveoCommerceTransport }),
    writable: false,
  });
})();
